/**
 * uploadQueue.ts
 *
 * IndexedDB-backed queue for vault uploads that failed while offline.
 * Stores the raw ArrayBuffer so the upload can be retried after the app
 * is reopened, even if the original File object is gone.
 *
 * Used by DataContext to enqueue failed uploads and drain them when
 * connectivity is restored — via the Background Sync API (Chrome/Edge)
 * or a plain `online` event listener (Firefox/Safari).
 */

const DB_NAME    = 'kodacare_offline';
const STORE      = 'pending_uploads';
const DB_VERSION = 1;

export interface PendingUpload {
  /** Re-uses the pre-generated Firestore doc ID so retries are idempotent. */
  id: string;
  petBase: string;
  uid: string;
  activePetId: string;
  firestoreDocId: string;
  storagePath: string;
  category: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  title: string | null;
  note: string | null;
  docDate: string | null;
  /** The file bytes — survives page close in IndexedDB. */
  buffer: ArrayBuffer;
  enqueuedAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
    req.onerror  = (e) => reject((e.target as IDBOpenDBRequest).error);
  });
}

export async function enqueueUpload(item: PendingUpload): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject((e.target as IDBRequest).error);
  });
}

export async function getPendingUploads(): Promise<PendingUpload[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result as PendingUpload[]);
    req.onerror   = (e) => reject((e.target as IDBRequest).error);
  });
}

export async function dequeueUpload(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror    = (e) => reject((e.target as IDBRequest).error);
  });
}
